/* eslint react/jsx-one-expression-per-line: off */

import React from 'react';
import { HashLink as Link } from 'react-router-hash-link';
import './Afterword.scss';
/* Afterword */
const Afterword = () => {
  return (
    <div className="afterword">
      <h1>Afterword</h1>

      <h2>I. Early Methods</h2>
      <p>
        In April of 1990, I received a postcard from Jackson Mac Low. He and I
        had been writing back and forth intermittently following his having
        taught at the university I was attending. In his note, Jackson asked if
        I had copies of his <em>French Sonnets</em> or{' '}
        <em>Words nd Ends from Ez</em>, and if not, would I like him to send
        them to me? I didn’t have either of the books and soon wrote back to say
        that I’d love to have them.
      </p>
      <p>
        It wasn’t long after replying to his postcard that I began wondering
        about the books now headed my way. In the past I’d noticed that the
        titles of Jackson’s books or sequences often bore some relation not only
        to the content of the poems, but also to the methods he’d employed in
        writing them.
        <Link smooth to="#footnote-1" className="footnote-number">
          <sup>1</sup>
        </Link>{' '}
        I already knew something about <em>Words nd Ends from Ez</em>, having
        heard Jackson speak of these poems and having read the ones included in{' '}
        <em>Representative Works: 1938-1985</em>, but I’d never heard of{' '}
        <em>French Sonnets</em>, so I became especially curious about the book.
        What about the poems would be French? In what ways would they be
        sonnets? I looked in local book stores and in the university’s library
        but couldn’t find a copy of the book. While I was waiting for the books
        to show up in the mail I kept thinking about the poems, and I began to
        imagine several methods Jackson might have used in writing them. Later
        on that same month I decided I would put one of these imagined methods
        to use myself.
      </p>
      <p>
        Several years earlier I’d stolen my mother’s copy of Charles
        Baudelaire’s <em>Les Fleurs du Mal</em>, an all-French edition
        <Link smooth to="#footnote-2" className="footnote-number">
          <sup>2</sup>
        </Link>{' '}
        put out by Doubleday in 1961. Opening the book I chose a poem at random:
        “Le Serpent Qui Danse.” Starting with the poem’s title, I went through{' '}
        <em>The Random House Collegiate Dictionary</em> looking for each of the
        words in the poem. Obviously, since I was using an English dictionary, I
        wasn’t able to find most of them. Instead I located the word that would
        have followed Baudelaire’s alphabetically, had it been in the
        dictionary. Once I’d located this English word I wrote it into my
        translation in the same location its French partner had occupied in the
        original. In this way in the title “Serpent” was replaced with
        “Serpent,” and “Danse” was replaced with “Dap.” I didn’t change proper
        nouns. When I located an English word for use and saw that it was noted
        in the dictionary as having French origins and that its spelling hadn’t
        changed when it crossed over to English, I substituted the word that
        followed it alphabetically in the dictionary.
        <Link smooth to="#footnote-3" className="footnote-number">
          <sup>3</sup>
        </Link>{' '}
        I began all this quite late at night and discovered that the method was
        rather time-consuming, so I went to bed with only the first two stanzas
        of the poem completed.
      </p>

      <h3>MWord</h3>
      <p>
        The next morning a second, faster method occurred to me. I realized
        that, rather than looking up each word in a dictionary, I could type one
        of Baudelaire’s poems—in French—into a word-processing program and using
        the “spell-check” feature allow the program to make English
        “suggestions” for each of the “misspelled” words. I could then choose to
        replace the French words with those suggested by the word processor. I
        selected Baudelaire’s “L’Aube Spirituelle” at random and typed it up
        using <em>Microsoft Word v.3.0</em>. I soon discovered, however, that
        when the program spell-checked the poem there were some French words for
        which the program wouldn’t offer an English suggestion. I went through
        the entire poem taking Word’s suggested substitutions whenever they were
        offered, but ended up with a series of lines written in both French and
        English. I translated the remaining French words directly into English
        using <em>Larousse’s French-English/English-French Dictionary</em>. The
        resulting lines were made up entirely of English words, but they lacked
        coherent syntax. At this point, in spite of my interest in using methods
        similar to this one as a way of getting away from the practices I’d
        employed as a poet up to that point, I didn’t feel completely satisfied
        with the result. My interest in having a strong hand in the poem and in
        narrative kept me from being content with the text as it stood.
      </p>
      <p>
        Later I came to think of this text—seemingly unconnected English
        words—as my “base text.” I used this “base text” as a sort of framework
        for the resultant poem. This isn’t to say that I would use each word
        from each line of the base text in strict order, but rather that I
        allowed the words to suggest lines of the poem to me, although
        individual words from the base text were sometimes directly
        incorporated. In this way the word “mess” in line 11 of “L’Aube
        Spiritually” comes from spell-checking the French “mes,” whereas my use
        of “electrifying” was suggested by “voltage,” the spell-checker’s
        substitution for the French “voltige” in the original.
      </p>
      <p>
        Much later, after I’d used this method a few times, I stopped using the
        French-English dictionary to translate words remaining in French after
        the spell-check was complete. Instead, I started translating these
        French words in a way which more closely resembled the{' '}
        <strong>As Understood</strong> and <strong>Free</strong> translation
        methods described below. After I’d written my first twenty translations
        I stopped using the <strong>MWord</strong> method much, though I did
        return to it from time to time when I needed to briefly change my
        practice.
      </p>

      <h3>Phonetic</h3>
      <p>
        For whatever reason, after writing “L’Aube Spiritually” I didn’t write
        another of these translations until late October of the same year. When
        I did decide to pick things up again I was nowhere near a computer.
        Since I couldn’t generate a base text using the familiar spell-check
        method, I chose to do so by trying to find English phonetic equivalents
        of Baudelaire’s French. Sounding out the words to “La Mort Des Amants”
        (which I’d again selected at random, of course
        <Link smooth to="#footnote-4" className="footnote-number">
          <sup>4</sup>
        </Link>
        ) I generated a base text of English words which, again, lacked any
        obvious syntax. I listened for English words in the sounds of individual
        words, strings of words, or any sequence of syllables in the words in
        Baudelaire’s lines. In this manner the beginning of the first line of
        “La Mort Des Amants,” “Nous aurons des lits…” was translated as “Noose
        orange delete.” Using these words as a jumping-off point, “An orange
        noose deleted…” became the beginning of the first line of “Law Morgue
        Days Almonds.”
      </p>

      <h3>As Understood</h3>
      <p>
        The next method that I came up with was far simpler than the previous
        two. I should back up a bit and say that when I began this project it
        had been about three years since my last French class. While my
        pronunciation might have been acceptable, there were large holes in my
        comprehension of French.
        <Link smooth to="#footnote-5" className="footnote-number">
          <sup>5</sup>
        </Link>{' '}
        In looking over several of Baudelaire’s poems however, I realized that I
        still understood a passable amount of what I was reading, and those
        words or phrases that I didn’t recognize were automatically assigned
        meanings, which they may or may not have had.
        <Link smooth to="#footnote-6" className="footnote-number">
          <sup>6</sup>
        </Link>{' '}
        Not unlike a poorly prepared student translating a passage he’s never
        seen before from French to English in an exam, I tried to make sense of
        the poems in front of me. Since I was trying to translate the phrases in
        their entirety, I didn’t need a base text to work from. At this point I
        was more interested in understanding the language in Baudelaire’s poems
        than just the individual, isolated words.
      </p>

      <h3>Word List</h3>
      <p>
        The fourth method I worked out was only used in two of my translations:
        “Ember” and “Checkroom.” I made three lists of words (one list each for
        nouns, verbs and adjectives) chosen at random from{' '}
        <em>The Random House Collegiate Dictionary</em>. Each list had
        twenty-six words that began with a different letter of the alphabet,
        making a table three columns wide and twenty-six rows long. Taking
        Baudelaire’s poems “Elevation” and “Causerie,” I replaced the French
        nouns, verbs and adjectives with nouns, verbs and adjectives from my
        word lists. I chose words that began with the letter corresponding with
        the 1st letter of each French word. So “Elevation” became “Ember,”
        because “Elevation” was a noun beginning with E, and “Ember” was the
        word in the noun column’s E row. Adverbs were mostly translated as
        adjectives; prepositions and conjunctions were translated literally; and
        articles were translated as I judged appropriate, because “la vie” could
        translate as “life” or “the life” in different contexts. While my
        adherence to use of punctuation had varied in methods employed prior to
        “Word List,” the punctuation in both “Ember” and “Checkroom” mimics that
        of Baudelaire’s originals, and there’s probably also a close adherence
        to the sentence structures of the originals, due to the very nature of
        such a one-to-one replacement.
      </p>

      <h3>
        Free
        <Link smooth to="#footnote-7" className="footnote-number">
          <sup>7</sup>
        </Link>
      </h3>
      <p>
        If it’s not already evident—after my confessing that I decided to use a
        word processor to find my French words’ nearest neighbors rather than
        search for the space they would each occupy in a dictionary—let me say
        straight out that I can become somewhat impatient as a writer.
        <Link smooth to="#footnote-8" className="footnote-number">
          <sup>8</sup>
        </Link>{' '}
        I found that as I translated poems <strong>As Understood</strong> or
        used the base texts generated with <strong>MWord</strong> or the{' '}
        <strong>Phonetic</strong> method, I wanted to move a little more quickly
        through the poems. New words and lines were suggesting themselves to me
        based on the meanings, sounds, shapes, possible meanings, and
        associations with the language in the French originals. So the{' '}
        <strong>Free</strong> method was developed by allowing myself to make
        use of these “suggestions” in what I came to think of as a “rapid
        jumping-off from the text.” While the translations I had made{' '}
        <strong>As Understood</strong> were written based on my best
        understanding of the poems, the <strong>Free</strong> translations
        responded more to characteristics of Baudelaire’s writing which were
        less obvious or overt. For example, the title of the poem “The Auto
        Mechanics,” translated <strong>Free</strong> from
        “L’Héautontimorouménos,” may have arrived as a result of the presence of
        “auto” in the longer French word, the length of the word and the other
        letters in Baudelaire’s title word, or perhaps something I am unaware
        of, to which I was responding.
      </p>
      <p>
        Since there is no grounding rule in the <strong>Free</strong> method, in
        the later translation work there was plenty of room to modify, respond
        or react both to modes of writing that I’d employed previously in the
        project and my experience of translating the poems.{' '}
        <strong>Free</strong> is the method used predominantly in Flowers of
        Bad, and there is no definitive version of it. Variations include{' '}
        <strong>Frigid</strong>, <strong>Frigidaire</strong>,{' '}
        <strong>Frozen</strong>, and <strong>Cracked</strong>. One poem was even
        translated using the <strong>Shazbot</strong> translation method. On the
        pages where I first wrote out my <strong>Free</strong> translations
        there are often notes like “What has Free become?” or “Free, but
        slowly,” or once or twice something like “Free-slurry.” There are
        probably numerous changes in my use of the <strong>Free</strong> method
        that could be noted if these translations were regarded chronologically,
        including breakdowns in syntax and in the one-to-one translation of
        words or images. Sometimes two or more meanings suggested themselves for
        lines or phrases in a poem, and rather than trying to figure out which
        of them was <em>le mot juste</em>, I would incorporate more than one,
        covering all my bets.
      </p>
      <p>
        While it’s true that many of the methods that came later were developed
        as direct responses to ones that had preceded them, I think of the
        creation of the of the first six methods (the discarded first one with
        the dictionary,{' '}
        <strong>MWord, Phonetic, Word List, As Understood, Free</strong>) as
        evolving more than responding. I say this because the development of
        each came more out of working with the previous methods and seeing other
        routes or possibilities than out of reacting against them in frustration
        or trying to find some way to get around problems that I’d come up
        against. The <strong>Free</strong> method grew mostly out of translating
        Baudelaire’s poems <strong>As Understood</strong>, although the other
        five methods contributed something to my use of it I’m sure.
      </p>

      <h2>II. Later Methods</h2>

      <h3>Collision & Directed</h3>
      <p>
        It has always seemed impossible to me to translate something without
        imposing some part of the translator’s own disposition. With this in
        mind, I decided that I wanted to intentionally choose a direction or
        attitude for a translation to adopt before I began composing it. The
        Collision method involved taking a poem of Baudelaire’s along with a
        second text, and translating the first either by using the second as a
        filter, or by smashing the two together during the translation.
      </p>
      <p>
        Somewhat similarly, the <strong>Directed</strong> method became another
        way of imposing a leaning or a tendency on a translation. In a{' '}
        <strong>Directed</strong> translation everything is interpreted in terms
        of a particular focus. If this focus were, for example, the object of
        one’s affection or a particular political agenda then it would be as if
        it were <em>known</em> that Baudelaire’s poem was in fact about the
        object of one’s affection or the political agenda. Any further
        understanding of Baudelaire’s poem would reflect this fact and this
        premise would be demonstrated in the translation of Baudelaire’s poem.
      </p>
      <h3>Blind</h3>
      <p>
        At some point I decided that I’d translated enough of Baudelaire’s poems
        that I felt as though I knew what he was doing, and I felt as though I
        understood pretty much what I was doing, so why not simply cut out the
        middle man? For the <strong>Blind</strong> translations I approached
        Baudelaire’s poem knowing nothing more at first than the line count and
        the title. Once I had written my translation I checked it against
        Baudelaire’s original and any parts of the poem that had been translated
        incorrectly were then corrected. (Remarkably, the handful of times that
        I used this method I found that my translations were pretty much dead-on
        and required very little correction.)
      </p>

      <h3>Forced Sonnet</h3>
      <p>
        In order to explain the <strong>Forced Sonnet</strong>
        <Link smooth to="#footnote-9" className="footnote-number">
          <sup>9</sup>
        </Link>{' '}
        method, it may be easiest to first describe the form of the resulting
        poem. The <strong>Forced Sonnets</strong> were translated from sonnets
        of Baudelaire’s. The translations all had 14 lines, followed the same
        rhyme scheme and each line of the translation had the same number of
        syllables as the corresponding line in the French poem.
      </p>
      <p>
        I would begin by taking a translation I had done of one of Baudelaire’s
        sonnets using any of the other methods I’ve described up to this point
        (let’s call this the <em>old translation</em>). Then from the beginning
        of the first line of my old translation, I counted out a number of
        syllables equal to the number of syllables in the first line of the
        original French poem, and struck out all words on the line that came
        after. The words that remained became the first line of the Forced
        Sonnet. I then followed the same procedure with the second line of my
        old translation as I had with the first to produce the second line of my{' '}
        <strong>Forced Sonnet</strong>.
      </p>
      <p>
        Starting at the beginning of the third line of the old translation, I
        would look for a word which rhymed with what was now the last word of
        the first line of the <strong>Forced Sonnet</strong>. I then counted
        backwards from the rhyming word on the third line until I had reached a
        syllable count equal to the number of syllables in the third line of
        Baudelaire’s original. Only the syllables within that count made up the
        third line of the <strong>Forced Sonnet</strong> and I would strike out
        all others from the line. I then followed the same procedure to produce
        the fourth line as I had with the third, beginning by seeking a word in
        fourth line of the old translation which rhymed with the last word of
        the second line of the <strong>Forced Sonnet</strong>.
      </p>
      <p>
        Following this procedure I would now have a four-line stanza of two
        rhyming couplets, A-B-A-B. From here I would go on to produce the
        remaining rhyming couplets of the <strong>Forced Sonnet</strong> (i.e.,
        C-D-C-D E-E-F G-G-F). This is the basic method. However, there were a
        few contingencies I discovered I needed to plan for.
      </p>
      <p>
        Let’s suppose that I found a word in the third line of the old
        translation which rhymed with the end word of the new first line of the{' '}
        <strong>Forced Sonnet</strong>. Now let’s suppose that counting
        backwards from that word in the old translation I arrived at the
        beginning of the line before I have matched the syllable count of the
        corresponding third line of Baudelaire’s poem. In this instance I would
        wrap my syllable count around to the end of the line, continuing to
        count backwards from the last word of the third line of the old
        translation. The last syllable in that count would become the beginning
        of the third line in the <strong>Forced Sonnet</strong> and the rhyming
        word—where we started our count—would become the end of the third line.
      </p>
      <p>
        As an example, let’s say my first line of my{' '}
        <strong>Forced Sonnet</strong> ended with the word “these” and the third
        line of my old translation is “The batteries on the furnace climbing
        higher and higher until.” The first word in this line that rhymes with
        “these” is “batteries.” But if the third line in Baudelaire’s poem has
        thirteen syllables I’m out of luck, because counting backwards from
        “batteries” is only going to give me four syllables: “The batteries.” So
        I’ll get my remaining nine syllables by continuing to count backwards
        from “until,” the last word of the same line in the old translation.
        Counting backwards nine syllables I get “Climbing higher and higher
        until.” I’ll strike all remaining words or syllables from the line and
        put these nine syllables before my first four (still gotta have
        “batteries” at the end of the line to make the rhyme) and get as the
        third line of my <strong>Forced Sonnet</strong>:
      </p>
      <p className="example">Climbing higher and higher until the batteries</p>
      <p>
        Now let’s suppose that there is no word in the third line of my old
        translation which rhymes with the end word of the first line of the{' '}
        <strong>Forced Sonnet</strong> I’m composing. In this case I would
        return to the first line of my old translation and rewrite it by
        starting the syllable count from the second word in the line. This
        should produce a different end-word for the first line of the{' '}
        <strong>Forced Sonnet</strong>. So then I would return to the third line
        of the old translation and try to find a rhyming match again. If again
        no rhyming match could be found, I would return to the first line of the
        old translation and begin my count with the third word, repeating the
        process until a match had been made.
      </p>
      <p>
        Finally, it may have occurred to the reader that the odds are good that
        the syllable count might at some point fall in the middle of a word. I
        approached this issue both possible ways, by composing{' '}
        <strong>Forced Sonnets</strong> that included portions of words and by
        extending the syllable count in order to accommodate entire words.
        However, I never used both approaches in any one{' '}
        <strong>Forced Sonnet</strong>.
      </p>

      <h3>The Two Good Sisters</h3>
      <p>
        <strong>The Two Good Sisters</strong> method gets its name from both the
        title of the poem this method was first used with (“Les Deux Bonnes
        Sœurs”) and because the lines of a poem translated this way are
        separated in the middle by a short gap, creating a poem that may look as
        though it’s made up of two distinct but related parts, one right next to
        the other.
      </p>
      <p>
        In employing <strong>The Two Good Sisters</strong>, I would take a
        rectangular and opaque object (i.e., a magazine or a book) and lay it
        over a poem of Baudelaire’s so that the object’s leftmost edge ran
        vertically down the center of the poem, covering up the right half of
        the poem from top to bottom. I then translated (using any of my other
        methods) only what remained uncovered, including those words that were
        only partially visible. Once the entire first half of every line in the
        poem had been translated this way, I covered only the left half of the
        poem and translated the right-hand part of the poem which was now
        visible.
      </p>
      <p>
        From there, I lined the two halves of the poem up alongside one another
        with a gap of about five em spaces. With one or two of the translations
        I composed using <strong>The Two Good Sisters</strong> I merged the two
        halves together by removing the gap so they had the appearance of being
        made up of unbroken lines. In other instances I attempted to resolve the
        two mismatched halves of the lines so that they seemed to make more
        sense narratively; in others I didn’t, allowing them to make their own
        sense.
      </p>
      <p>
        Another variation of this form worth mentioning is{' '}
        <strong>The One Blind Sister</strong>, where one half of the poem was
        translated using <strong>The Two Good Sisters</strong> method and the
        other was translated using the <strong>Blind</strong> method (as
        explained above).
      </p>

      <h3>Exclusion</h3>
      <p>
        Somewhat similar in appearance, but not to be mistaken for{' '}
        <strong>The Two Good Sisters</strong> is the <strong>Exclusion</strong>{' '}
        method. The two might easily be mistaken because of a similarity in
        their appearances: both may include a visible split in the poem’s lines.
      </p>
      <p>
        The <strong>Exclusion</strong> method was accomplished in two stages. In
        the first stage of the translation process, I allowed myself not to feel
        bound to making use of all aspects of Baudelaire’s original poem. I took
        instead whichever parts of the lines I wanted and disregarded the rest.
        I tried, however, to insure that the result of this first stage would be
        a poem which would cohere narratively as the reader read in normal
        fashion, left to right, top to bottom. In the second stage of the{' '}
        <strong>Exclusion</strong> method I returned to the beginning of
        Baudelaire’s poem and wrote separate translations of the portions of the
        lines I’d excluded in the first stage, without trying to force the
        translated portions to reflect or cohere with any other part of the
        poem. Once the excluded portions had all been translated, I placed them
        next to the corresponding lines of the first part of the translation in
        italics, enclosed within parentheses.
      </p>

      <h3>Swimming</h3>
      <p>
        This may be the most difficult of the methods to describe exactly or
        specifically. I called the method <strong>Swimming</strong> because this
        is how I thought of it at the time: as swimming on the surface of the
        poem. Generally I would write out in French one of Baudelaire’s poems by
        hand, in blocks of about four to six lines. Then, using a different
        color ink, above or below the copied lines, I would write lines which
        somehow captured the mood of the French, while paying as little direct
        attention as I could to those qualities of the language that I had paid
        attention to in earlier methods (meanings, sounds, shapes, etc.). I
        repeated this process until all the lines from Baudelaire’s poem had
        been written out in French and translated in order.
      </p>

      <h3>Anagrammatic</h3>
      <p>
        I had intended for the <strong>Anagrammatic</strong> translations to be
        the last method I employed. By the time I reached the point where there
        were only five untranslated poems I had developed many feelings about
        the practice and process of translating, and about what I had and had
        not been able to accomplish during the long project of working with{' '}
        <em>Les Fleurs Du Mal</em>. I regretted that in my translations so many
        things had been added and so many things lost. I wanted somehow to make
        use of all of a poem without losing or adding anything.
      </p>
      <p>
        The closest that I came to this ideal may be in the{' '}
        <strong>Anagrammatic</strong> translations (there are 4), as every
        letter that appears in the original French poem also appears in the
        translation. No letters were added. These translations have titles which
        are anagrams of the French originals’ and every poem is a line-by-line
        anagram of its French counterpart. I didn’t retain any of the accents or
        cedillas, and letters such as œ were converted into o and e, but with
        these exceptions each line in the translation contains the same letters
        as the original.
      </p>
      <p>
        The first poem I translated using the <strong>Anagrammatic</strong>{' '}
        method used a variant that clearly follows the toughest set of
        restrictions, bordering on the Oulipian.
        <Link smooth to="#footnote-10" className="footnote-number">
          <sup>10</sup>
        </Link>{' '}
        In “A.M. Sequel,” not only is every line, dedication and epigram
        anagrammatic of the original, but the poem is made up of complete
        sentences which follow the rules of English grammar (although there
        continues to be some debate between my copy-editor mother and myself as
        to whether a single noun can be modified by two verbs without a
        conjunction). All of the <strong>Anagrammatic</strong> translations were
        written by laying out letters from the lines of one of Baudelaire’s
        poems using the tiles from two complete <em>Scrabble</em>™ sets and then
        rearranging them. I did this steadily for about three and a half months
        in order to write “A.M. Sequel,” and then swore I would never take up
        this particular variation of the method again.
      </p>
      <p>
        As I’ve just written, when I began the <strong>Anagrammatic</strong>{' '}
        poems I had many complex feelings about translating and more
        specifically about translating Baudelaire. I also felt that composing
        false translations had often been a way of finding entry into a poem
        from which I was otherwise excluded. From time to time, however, I no
        longer wanted so much to find a way into the poems as to shatter them
        with a hammer. Though I tried to figure out just how this could be
        accomplished (“1. Write poem out on a sheet of glass. 2. Apply hammer as
        needed.”, etc.) I never fully succeeded. I do think, however, that the
        remaining three <strong>Anagrammatic</strong> translations (“Hep
        Slears,” “XLII” & “Er Irbralaple”) come closest to accomplishing this
        out of all the translations that I’ve done.
        <Link smooth to="#footnote-11" className="footnote-number">
          <sup>11</sup>
        </Link>
      </p>

      <h3>Re-translation</h3>
      <p>
        There at the last though, I found that there were a few poems that I
        wanted to re-translate, although I found the methods that I’d used up to
        that point unworkable, perhaps because I was quite ready to be finished
        with the project. I tried working with the texts as they were, colliding
        them with alternate translations of the same poem, extracting words,
        etc. The results, however, were not terribly satisfying. Ultimately I
        ended up re-translating them, but without referring again to
        Baudelaire’s original. Two of these (there were three, I believe) were
        translated from English into English, using a method which probably owes
        something to the <strong>Swimming</strong> method. The third of these{' '}
        <strong>Re-translations</strong> I translated back into French. Having
        read all of Baudelaire’s <em>Les Fleurs du Mal</em> in one fashion or
        another, I had picked up or remembered a bit more French than I started
        with. If nothing else I might say that I felt a certain familiarity with
        the language, even if I wouldn’t be able to share that familiarity with
        anyone else by engaging them in a conversation.
        <Link smooth to="#footnote-12" className="footnote-number">
          <sup>12</sup>
        </Link>{' '}
        I decided that I would do my best to restore one of Baudelaire’s poems
        by re-translating it from English back into French in a way which may
        serve as the obverse of the <strong>As Understood</strong> method.
      </p>

      <h2>III. A Final Note On The Forms Of These Poems</h2>
      <p>
        With few exceptions I have managed from the beginning to retain several
        aspects of Baudelaire’s form in each of these poems. Firstly, almost
        every line in this collection begins with an initial cap, as do the
        lines in the edition of <em>Les Fleurs du Mal</em> that I have used
        throughout the process. For the most part I have also followed
        Baudelaire’s line breaks and stanza length. His sonnets remain sonnets
        here, at least in as much as they are fourteen-line poems in two
        four-line stanzas followed by two three-line stanzas.
        <Link smooth to="#footnote-13" className="footnote-number">
          13
        </Link>{' '}
        While all these poems were translated as whole and complete poems, the
        translations were done line by line, and the translated aspects or words
        from one line do not wrap around and appear on another that follows it.
        There are also those poems that retain the rhyme schemes of the
        originals, including some that are not <strong>Forced Sonnets</strong>.
      </p>
      <p>
        In March of 1991, almost two weeks after I’d written “The Fin of the
        Journey,” the last poem in the first sequence of twenty poems from this
        series which would later be published as <em>Flurries of Mail</em>, a
        copy of <em>French Sonnets</em> that Jackson had sent arrived in the
        mail.
      </p>

      <p>
        <br />
        David Cameron
        <br />
        Toronto, Ontario
        <br />
        December 22, 2001
      </p>

      <h2 className="footnotes-section">Footnotes</h2>
      <ol>
        <li id="footnote-1">
          <p>
            The title of <em>Words nd Ends from Ez</em>, for example, mimics
            something of the appearance of the lines of the poems included in
            that collection where Jackson “systematically brought into
            <em>Words nd Ends from Ez</em>
            letter strings consisting of single words and/or ends of words [from
            <em>The Cantos</em>] that successively ‘spell out’ Ezra Pound’s
            first and last name ‘diastically,’ i.e., strings in which the
            letters of Pound’s first and last names occupy places corresponding
            to those they fill in the names.” [<em>Words nd Ends from Ez</em>,
            p. 89]
          </p>
        </li>
        <li id="footnote-2">
          <p>
            The only other all-French book of poems that I had in my apartment
            at the time was Jacques Prevert’s <em>Paroles</em>. I imagine that
            this would now be a very different collection if I’d chosen to make
            use of it rather than <em>Les Fleurs du Mal</em>. You might very
            well be holding a book right now entitled <em>Parolees</em> or{' '}
            <em>Pay Roll</em> instead.
          </p>
        </li>
        <li id="footnote-3">
          <p>
            Which leaves us with the mystery of Serpent, which would seem to be
            a French word which hasn’t changed its spelling. Sixteen years later
            I seem to have lost track of my copy of{' '}
            <em>The Random House Collegiate Dictionary</em>, so I’m unable to
            refer back to exactly what it said. However, other dictionaries that
            I have in my possession at present indicate that “serpent” is Middle
            English in origin, and this may be why I chose to keep it in. Or
            maybe I ignored my rule or hadn’t come up with it before I started
            out writing the translation but found a need to apply it later on so
            developed it on-the-fly. Christ, it was only eight lines I got
            through that night. In any event I was probably quite tired and
            should be cut a small measure of slack for that reason if no other.
          </p>
        </li>
        <li id="footnote-4">
          <p>
            Of course, choosing a poem “at random” usually meant opening the
            book somewhere near the middle and using whichever poem was on the
            right hand page, so long as it wasn’t too lengthy for however much
            time I wanted to spend writing right then.
          </p>
        </li>
        <li id="footnote-5">
          <p>
            Maybe it would be good to include some note about how much French I
            speak now or had studied up to that point. I had taken about three
            years of French in high school, and was pretty fanatical about it. I
            loved studying French, and did very well with it. When I got to
            college I continued my studies, beginning with a French intensive
            class and then two more classes over the next two terms, the last
            one being a class in French phonetics and pronunciation. I left the
            university I was attending after that third term, largely because
            the academic mood was somewhat lacking there and my performance
            academically had begun to slip at the end of my first year. I didn’t
            fail anything, but my studies of French weren’t really proceeding
            with the same vigor that they had when I was in high school. After I
            got to Binghamton, where I later studied with Jackson, I really
            didn’t feel much like continuing with my French studies. Just before
            I met Jackson, I had taken a term and a half of introductory
            Spanish, taken largely because I wanted to be able to better hear
            the language in Federico Garcia Lorca’s poems. As much as I may have
            wanted to return to my studies of French once I’d begun this
            project, I felt as though I shouldn’t try to learn any more French
            as it would make the process of translating, especially with the{' '}
            <strong>As Understood</strong> and <strong>Free</strong> methods,
            that much more difficult. I have only ever read one paragraph of a
            quotation of Baudelaire’s in translation. I have a good
            understanding of French grammar and at least a fair understanding of
            the phonetics (although I might not be able to get my tongue to work
            out all pronunciations as I would like it to). What is probably most
            lacking is my vocabulary. A somewhat more concrete example of how
            lacking or not my French is might be found in the poem “Blanche.”
          </p>
        </li>
        <li id="footnote-6">
          <p>
            In explaining this process to people since, I’ve pointed out that
            this is the sort of thing that goes on all the time. I think that
            when people encounter language they don’t understand they
            automatically make some sense of it, even if they write these
            interpretations off because they don’t match the rest of their
            understanding.
          </p>
        </li>
        <li id="footnote-7">
          <p>
            How shall I explain the <strong>Free</strong> method now as I hurtle
            towards Toronto in a train to visit my grandmother just before
            Christmas? Canada’s a strange place, where most of the product
            packaging (if not all, I haven’t inspected it all yet) is written
            out in both French and English. My earliest desires to study French
            arose out of a frustration with this country, where left to my own
            devices while the adults busied themselves with adult matters (all
            that talking!) I was allowed to sit down in my grandmother’s study
            and watch television. Is there some sort of metaphor for the{' '}
            <strong>Free</strong> process or for all of false translation in the
            fact that across North America it seems that the major television
            stations broadcast much of the same programming but at different
            times and on differently numbered channels? My grandmother’s house
            possessed a strange device known as a cable box. I don’t think that
            I’ve ever seen one outside of Canada like it. It was a small box,
            covered with buttons, each designated with a number and a few
            letters, probably including ABC, NBC and PBS, but all with the
            numbers wrong. Since all the stations were wrong, were not where
            they should have been, I had to punch through this series of buttons
            trying to uncover the code that would show me the sort of
            programming I most desired: cartoons. Because of the fervor with
            which I searched, I don’t think that it took too very long to locate
            a channel showing cartoons, but my discovery was accompanied by a
            powerful frustration. The cartoons were entirely in French. Bugs and
            Elmer were speaking, but the words that came out of their mouths
            were entirely incomprehensible to me. At that point I became
            determined to learn to speak French, so I could return to Toronto
            and understand the French cartoons.
          </p>
          <p>
            So it would be possible, I suppose, to argue that the origins of
            these false translations comes earlier than when I received
            Jackson’s post card. Fairly late into the project I began to see one
            possible way of interpreting this project as a struggle to find a
            way into a text from which I was otherwise excluded. Often enough,
            this is why I had to switch up or modify my methods, or develop new
            ones. After working with a method for a while I would find myself
            stalling out, I would worry that I was falling back on tricks or
            ways that I already knew would work, and the poems would no longer
            surprise me in the way that I wanted them to. I am reminded here,
            oddly enough, of the <em>Star Trek</em> villains The Borg, who could
            be hit by the Enterprise’s phaser blasts, but only for a couple of
            shots, at which point their shields adapted, and the phasers would
            no longer be useful. So the crew of the Enterprise found that they
            had to continually change the modulation of their phasers so that
            they would be able to fight off the Borg and resist being
            assimilated into their “collective,” an organization that thought
            with one mind, acted as one entity, had only one understanding.
            Although I wonder if this contradicts a statement by Walt Whitman
            that I’ve often also associated with these poems “The job of the
            poet to resolve all tongues into his own.” Though that’s “resolve,”
            and not “assimilate.”
          </p>
        </li>
        <li id="footnote-8">
          <p>
            Although the three and a half months it took to write the{' '}
            <strong>Anagrammatic</strong> “A.M. Sequel” might seem to contradict
            that.
          </p>
        </li>
        <li id="footnote-9">
          <p>
            This description assumes an A-B-A-B first stanza. It should be
            modified for other Sonnet forms.
          </p>
        </li>
        <li id="footnote-10">
          <p>
            It’s ridiculous to assume that I’m going to be able to explain to
            you in full what the Oulipo is or what I mean by Oulipian here, so
            let’s abandon that as the goal of this footnote. Rather I’ll refer
            you to a few other people’s words on the subject. In{' '}
            <em>The Oulipo and Combinatorial Art</em> (1991) Jacques Roubaud
            explains that the Oulipo is a group whose name derives from their
            “initial, inaugural name: Ouvroir de Littérature Potentielle”
            (Workshop for Potential Literature). He further goes on to explain
            that “The aim of Oulipo is to invent (or reinvent) restrictions of a
            formal nature and propose them to enthusiasts interested in
            composing literature.” Raymond Queneau’s definition is my favorite:
            “Oulipians: rats who build the labyrinth form which they plan to
            escape.” In short, when I say “borders on the Oulipian” I mean that
            the form I was using had several rigid constraints which affected
            the resulting poem. In truth my constraints were more restrictive I
            believe than many of the methods created by the Oulipians, though
            certainly not as restrictive as some.
            <em>La Disparition</em> is a 300 page novel, written in 1969 by
            Georges Perec, entirely without the letter e. Its translation into
            English by Gilbert Adair, also entirely without the letter e, is
            entitled <em>A Void</em>.
          </p>
          <p>
            I first encountered the Oulipo around 1999, when Harry Matthews came
            to read at the St. Marks Poetry Project in New York shortly after
            the publication of <em>Oulipo Compendium</em>. I later wrote Mr.
            Matthews trying to learn whether my <strong>Anagramatic</strong>{' '}
            method had already been developed by any members of the Oulipo. He
            assured me that it had not.
          </p>
        </li>
        <li id="footnote-11">
          <p>
            Ironically this is done without losing any of the “shards” or
            “fragments” of the original.
          </p>
        </li>
        <li id="footnote-12">
          <p>
            I have been asked to include the following tale as an example of how
            well I actually speak French:
          </p>
          <p>
            Some years ago, on my first visit to France I went to Avignon in the
            South. While walking across a bridge that spanned the Rhône and a
            small park on one bank I was distracted by a group of older men and
            women playing boules on the grass below. With my head turned to the
            side and not watching where I was going I walked straight into a
            lamppost and banged my head so hard that it made a loud “bong!”
            sound against the metal pole, not unlike the sound of a medium-sized
            bell being struck. When I recovered from the collision I could see
            that all the people down in the park below me had paused in their
            game and were now looking straight up at me, trying to determine
            what had caused the noise.
          </p>
          <p>
            Of course I knew that I was going to get a large bump on my head and
            thought that as self-conscious as I was being an American travelling
            alone in France, being an American travelling alone in France with a
            large egg-shaped protrusion on one side of his head would be far
            worse. I determined immediately that I needed to remedy the
            situation and set out in search of a bar.
          </p>
          <p>
            It was late afternoon and fortunately there was one open just past
            the end of the bridge. I walked in and got the attention of the
            bartender and he came over, staring fixedly at my growing lump. Now
            that I had his attention I asked him (in French) “Can I have some
            ice cream? I’ve just kicked myself in the head with a lamppost.”
          </p>
        </li>
        <li id="footnote-13">
          <p>
            There are a couple of exceptions to this “sonnet rule” which were
            translated in reverse, from the bottom up, so the line order in
            those stanzas runs three-three-four-four, or where the line order
            within each stanza remains the same but where the order of the
            stanzas is reversed.
          </p>
        </li>
      </ol>
    </div>
  );
};

Afterword.propTypes = {};

Afterword.defaultProps = {};

export default Afterword;
/* */
